import { Component, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { form, FormField, required, email, pattern, maxLength } from '@angular/forms/signals';
import { ETipoPermiso } from '@core/enums/tipoPermiso.enum';
import { IEmpleadoData } from '@core/interfaces/entidades/empleado.entidad';
import { GrupoPermisoService } from '@shared/services/grupo-permiso.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { FormErrorMessagePipe } from '../../../pipes/form-error-message.pipe';
import { UiPassword, UiButton, UiSelect, UiTooltip, UiInputMask } from '@shared/components/ui';
import { EmpleadoApiService } from '@core/services/api/empleado-api.service';
import { ToastService } from '@shared/services/toast.service';
import { buildDiff, normalizarTelefono } from '@shared/utils/functions.utils';
import { environment } from '@env/environment';
import { FormsModule } from '@angular/forms';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';

@Component({
  selector: 'app-editar-empleado',
  imports: [
    FormsModule,
    FormField,
    FormErrorMessagePipe,
    UiPassword,
    UiButton,
    UiSelect,
    UiTooltip,
    UiInputMask,
  ],
  templateUrl: './editar-empleado.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './editar-empleado.css',
})
export class EditarEmpleado implements OnInit {
  ngOnInit(): void {
    const emp = this.empleado();
    this.empleadoForm().value.set({
      nombre_completo: emp.nombre_completo,
      email: emp.email,
      contra: '',
      sucursal_id: emp.sucursal_id ?? null,
      permisos: [...emp.permisos],
    });
    this.telefono = emp.telefono ? normalizarTelefono(emp.telefono) : undefined;

    this.opcionesSucursal.set([
      { label: 'Todas las sucursales', value: null },
      ...this.sucursalService.sucursales().map((s) => ({ label: s.nombre, value: s.id })),
    ]);
  }
  // datos de ingreso
  sucursalService = inject(SucursalService);
  opcionesSucursal = signal<IOptionsSelect<number | null>[]>([]);
  telefono?: string;
  patternPassword = String(environment.strongPasswordRegex);
  showPasswordChange = false;
  empleado = input.required<IEmpleadoData>();
  empleadoForm = form(
    signal({
      nombre_completo: '',
      email: '',
      contra: '',
      sucursal_id: null as number | null,
      permisos: [] as ETipoPermiso[],
    }),
    (schemaPath) => {
      required(schemaPath.nombre_completo, { message: 'El nombre completo es obligatorio.' });
      required(schemaPath.email, { message: 'El email es obligatorio.' });
      email(schemaPath.email, { message: 'El email no es válido.' });
      pattern(schemaPath.contra, environment.strongPasswordRegex, {
        message:
          'La contraseña debe tener al menos 8 caracteres, incluyendo una letra mayúscula, una letra minúscula y un número.',
      });
      pattern(schemaPath.nombre_completo, /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]+$/, {
        message: 'El nombre solo puede contener letras y espacios.',
      });
      maxLength(schemaPath.nombre_completo, 50, {
        message: 'El nombre no puede exceder los 50 caracteres.',
      });
    },
  );
  private checkInvalidForm(): boolean {
    if (
      this.empleadoForm().invalid() ||
      (this.showPasswordChange && this.empleadoForm().controlValue().contra.length < 8)
    ) {
      this.markAsAllTouched();
      this.toastService.show(
        'top-right',
        'warn',
        'Formulario inválido',
        'Por favor, revise los campos.',
      );
      return true;
    }
    return false;
  }
  private markAsAllTouched() {
    this.empleadoForm.nombre_completo().markAsTouched();
    this.empleadoForm.email().markAsTouched();
    if (this.showPasswordChange) {
      this.empleadoForm.contra().markAsTouched();
    }
  }
  // services
  empleadoApiService = inject(EmpleadoApiService);
  grupoPermisoService = inject(GrupoPermisoService);
  toastService = inject(ToastService);

  // evento emit
  cerrar = output<IEmpleadoData | undefined>();
  onCerrar(data?: IEmpleadoData) {
    this.cerrar.emit(data);
  }
  onTogglePermiso(permiso: ETipoPermiso, event: any) {
    if (event.target.checked) {
      const permisosSeleccionados = this.empleadoForm().controlValue().permisos;
      if (!permisosSeleccionados.includes(permiso)) {
        permisosSeleccionados.push(permiso);
      }
    } else {
      this.empleadoForm().value.set({
        ...this.empleadoForm().controlValue(),
        permisos: this.empleadoForm()
          .controlValue()
          .permisos.filter((p) => p !== permiso),
      });
    }
  }

  onTogglePasswordChange() {
    this.showPasswordChange = !this.showPasswordChange;
    if (!this.showPasswordChange) {
      this.empleadoForm().value.set({
        ...this.empleadoForm().controlValue(),
        contra: '',
      });
    }
  }

  limpiarPermisos() {
    this.empleadoForm().value.set({
      ...this.empleadoForm().controlValue(),
      permisos: [],
    });
  }

  guardar($event: Event) {
    $event.preventDefault();
    if (this.checkInvalidForm()) return;

    const { contra, ...currentValues } = this.empleadoForm().controlValue();

    const dataToSave = {
      ...currentValues,
      telefono: this.telefono ? normalizarTelefono(this.telefono) : undefined,
      ...(this.showPasswordChange && contra ? { contra } : {}),
    };
    const rq: Partial<IEmpleadoData> = buildDiff(this.empleado(), dataToSave);
    if (Object.keys(rq).length === 0) {
      this.toastService.show('top-right', 'info', 'Sin cambios', 'No se han realizado cambios.');
      return;
    }

    this.empleadoApiService.editar(this.empleado().id, rq).subscribe({
      next: (resp) => {
        this.toastService.show('top-right', 'success', 'Éxito', 'Empleado editado correctamente.');
        this.onCerrar(resp);
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al editar el empleado.',
        );
      },
    });
  }
}